import { useCallback } from 'react';

import { di } from 'react-magnetic-di';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import {
	skillCreateFlowSession,
	type SkillCreateFlowSessionState,
} from './skill-create-flow-session';

const SOLUTION_CREATE_FLOW_ACTION_SUBJECT = 'solutionCreateFlow';
const SKILL_DRAFT_CARD_ACTION_SUBJECT = 'solutionCreateFlowDraft';
const SKILL_CREATED_ACTION_SUBJECT = 'solutionCreateFlowSkill';
const ANALYTICS_OBJECT_TYPE = 'skill';
// Every skill create flow event goes through one channel. The atlaskit channel's listener only accepts UI events, so it cannot carry the track event.
const AI_MATE_CHANNEL = 'aiMate';
// The atlaskit listener used to add this tag, so it has to be set explicitly now.
const ATLASKIT_TAGS = ['atlaskit'];

export type SkillCreateFlowTouchPoint =
	| 'manualCreationLinks'
	| 'studioUniversalCreate'
	| 'studioCustomSkillsPage'
	| 'chatImportSkillButton'
	| 'chatSkillListBuildSkill'
	| 'chatAutoSuggestBuildSkill'
	| 'chatTypeAheadBuildSkill'
	| 'chatCreateSkillWithChat'
	| 'chatCreateSkillWithEditor'
	/** The chat drafted a skill on its own, rather than from a create-skill entry point. */
	| 'chatImplicitCustomSkill';

// The only touch points that can lead to a skill draft card being shown.
const SKILL_DRAFT_CARD_TOUCH_POINTS: readonly SkillCreateFlowTouchPoint[] = [
	'chatSkillListBuildSkill',
	'chatAutoSuggestBuildSkill',
	'chatTypeAheadBuildSkill',
	'chatCreateSkillWithChat',
	'chatCreateSkillWithEditor',
	'chatImplicitCustomSkill',
];

// The touch points whose session is reported on the skill created event.
const SKILL_CREATED_TOUCH_POINTS: readonly SkillCreateFlowTouchPoint[] = [
	'studioCustomSkillsPage',
	'studioUniversalCreate',
	'manualCreationLinks',
	'chatCreateSkillWithEditor',
];

const isReportableSession = (
	session: SkillCreateFlowSessionState,
	fromDraftCard: boolean,
): boolean => {
	if (fromDraftCard) {
		// A draft card's session has moved on to `draftCardViewed` by the time its skill is created.
		return (
			(session.stage === 'started' || session.stage === 'draftCardViewed') &&
			SKILL_DRAFT_CARD_TOUCH_POINTS.includes(session.touchPoint)
		);
	}

	return session.stage === 'started' && SKILL_CREATED_TOUCH_POINTS.includes(session.touchPoint);
};

export const useCustomSkillsAnalytics = (): {
	fireSkillCreateFlowViewed: (touchPoint: SkillCreateFlowTouchPoint) => void;
	fireSkillCreateFlowStart: (touchPoint: SkillCreateFlowTouchPoint) => void;
	fireSkillDraftCardViewed: () => void;
	hasSkillDraftCardSession: () => boolean;
	fireSkillCreated: (options?: { fromDraftCard?: boolean }) => void;
	fireSkillCreateFailed: (options?: { fromDraftCard?: boolean }) => void;
} => {
	di(useAnalyticsEvents);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireSkillEvent = useCallback(
		(
			actionSubject: string,
			action: 'viewed' | 'started' | 'created' | 'failed',
			attributes: { touchPoint?: SkillCreateFlowTouchPoint; traceId?: string },
			eventType?: 'track',
		) => {
			createAnalyticsEvent({
				...(eventType && { eventType }),
				tags: ATLASKIT_TAGS,
				action,
				actionSubject,
				attributes: {
					objectType: ANALYTICS_OBJECT_TYPE,
					...attributes,
				},
			}).fire(AI_MATE_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	// Each view is a new pass through the flow, so a new session and a new trace id every time.
	const fireSkillCreateFlowViewed = useCallback(
		(touchPoint: SkillCreateFlowTouchPoint) => {
			const { traceId } = skillCreateFlowSession.start(touchPoint, 'viewed');

			fireSkillEvent(SOLUTION_CREATE_FLOW_ACTION_SUBJECT, 'viewed', { touchPoint, traceId });
		},
		[fireSkillEvent],
	);

	// Starting from the touch point the flow was viewed from continues that session, sharing its
	// trace id. Starting from anywhere else, or without a session, begins a new one.
	const fireSkillCreateFlowStart = useCallback(
		(touchPoint: SkillCreateFlowTouchPoint) => {
			const { traceId } = skillCreateFlowSession.startOrContinue(touchPoint, 'started');

			fireSkillEvent(SOLUTION_CREATE_FLOW_ACTION_SUBJECT, 'started', { touchPoint, traceId });
		},
		[fireSkillEvent],
	);

	// Takes no touch point: the draft card does not know what led the user there, so both attributes
	// come from the session, and neither is reported without one. Whether that session could have led
	// here is for the caller to settle with `hasSkillDraftCardSession` first.
	const fireSkillDraftCardViewed = useCallback(() => {
		const session = skillCreateFlowSession.get();

		skillCreateFlowSession.advanceTo('draftCardViewed');
		fireSkillEvent(SKILL_DRAFT_CARD_ACTION_SUBJECT, 'viewed', {
			...(session && { traceId: session.traceId, touchPoint: session.touchPoint }),
		});
	}, [fireSkillEvent]);

	// Whether a started flow that could lead to a draft card is in progress. Being started is what
	// produces a draft card, so a flow only viewed does not count, and nor does one already followed
	// to a draft card — in both cases the next card starts a flow of its own.
	const hasSkillDraftCardSession = useCallback(() => {
		const session = skillCreateFlowSession.get();

		return (
			session?.stage === 'started' && SKILL_DRAFT_CARD_TOUCH_POINTS.includes(session.touchPoint)
		);
	}, []);

	// The session is only reported while started from one of the touch points that lead to a skill
	// being published.
	const fireSkillCreateOutcome = useCallback(
		(action: 'created' | 'failed', fromDraftCard = false) => {
			const session = skillCreateFlowSession.get();
			// A draft card's session has moved on to `draftCardViewed` by the time its skill is created.
			const reportedSession =
				session && isReportableSession(session, fromDraftCard) ? session : undefined;

			fireSkillEvent(
				SKILL_CREATED_ACTION_SUBJECT,
				action,
				reportedSession
					? { traceId: reportedSession.traceId, touchPoint: reportedSession.touchPoint }
					: {},
				'track',
			);
		},
		[fireSkillEvent],
	);

	// The skill exists, so the session ends here: the next flow begins a new pass with a new trace id.
	// `fromDraftCard` reports the session only if it is from a touch point that leads to a draft card.
	const fireSkillCreated = useCallback(
		({ fromDraftCard }: { fromDraftCard?: boolean } = {}) => {
			fireSkillCreateOutcome('created', fromDraftCard);
			skillCreateFlowSession.clear();
		},
		[fireSkillCreateOutcome],
	);

	// Leaves the session as it is, so a retry from the same form continues the same pass.
	const fireSkillCreateFailed = useCallback(
		({ fromDraftCard }: { fromDraftCard?: boolean } = {}) => {
			fireSkillCreateOutcome('failed', fromDraftCard);
		},
		[fireSkillCreateOutcome],
	);

	return {
		fireSkillCreated,
		fireSkillCreateFailed,
		fireSkillCreateFlowViewed,
		fireSkillCreateFlowStart,
		fireSkillDraftCardViewed,
		hasSkillDraftCardSession,
	};
};
